import { useEffect, useMemo, useState } from "react";
import { PhoneCall, Clock, Calendar, User, Pill } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ResultBadge, EnumBadge } from "@/components/badges";
import { TranscriptView } from "@/components/transcript-view";
import { StructuredOutputView } from "@/components/structured-output-view";
import { formatDuration, formatPersonName, formatDate } from "@/lib/formatters";
import { DATE_FORMAT_FULL } from "@/lib/constants";
import type { CallRow, CaseRecord } from "@/types";

// ─── Shared sub-component ────────────────────────────────────────────

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof PhoneCall;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium">{value || "-"}</span>
    </div>
  );
}

// ─── Attempt Selector ────────────────────────────────────────────────

interface AttemptSelectorProps {
  attempts: { success?: string }[];
  value: number;
  onChange: (idx: number) => void;
}

function AttemptSelector({ attempts, value, onChange }: AttemptSelectorProps) {
  if (attempts.length <= 1) return null;

  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">
        Attempt
      </label>
      <Select
        value={String(value)}
        onValueChange={(v) => onChange(Number(v))}
      >
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {attempts.map((so, i) => (
            <SelectItem key={i} value={String(i)}>
              Attempt {i + 1} — {so.success === "true" ? "Success" : "Failed"}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ─── Call Info Section ───────────────────────────────────────────────

interface CallInfoSectionProps {
  phoneNumber: string;
  duration?: number;
  timestamp: unknown;
  scheduledFor?: string;
}

function CallInfoSection({
  phoneNumber,
  duration,
  timestamp,
  scheduledFor,
}: CallInfoSectionProps) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold">Call Info</h4>
      <InfoRow icon={PhoneCall} label="Phone" value={phoneNumber} />
      <InfoRow icon={Clock} label="Duration" value={formatDuration(duration)} />
      <InfoRow icon={Calendar} label="Time" value={formatDate(timestamp, DATE_FORMAT_FULL)} />
      {scheduledFor ? (
        <InfoRow
          icon={Calendar}
          label="Scheduled to ring"
          value={formatDate(scheduledFor, DATE_FORMAT_FULL)}
        />
      ) : null}
    </div>
  );
}

// ─── Patient Info Section ───────────────────────────────────────────

interface PatientInfoSectionProps {
  name: string;
  dob: string;
  memberId: string;
  payer: string;
  medication: string;
}

function PatientInfoSection({ name, dob, memberId, payer, medication }: PatientInfoSectionProps) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold">Patient Info</h4>
      <InfoRow icon={User} label="Name" value={name} />
      <InfoRow icon={User} label="DOB" value={dob} />
      <InfoRow icon={User} label="Member ID" value={memberId} />
      <InfoRow icon={User} label="Payer" value={payer} />
      <InfoRow icon={Pill} label="Medication" value={medication} />
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────

interface CallDetailSheetProps {
  callRow: CallRow | null;
  onClose: () => void;
}

export function CallDetailSheet({ callRow, onClose }: CallDetailSheetProps) {
  const [attemptIdx, setAttemptIdx] = useState(0);

  const caseData = callRow?.case as CaseRecord | undefined;
  const soHistory = caseData?.structured_outputs_history ?? [];
  const txHistory = caseData?.call_transcript_history ?? [];

  useEffect(() => {
    if (callRow) {
      setAttemptIdx(callRow.attemptIndex);
    }
  }, [callRow?.rowKey, callRow?.attemptIndex, caseData?.id]);

  const currentSo = soHistory[attemptIdx];

  const currentTx = useMemo(() => {
    const list = txHistory;
    const cid = currentSo?.call_id;
    if (cid) {
      const match = list.find((t) => t.call_id === cid);
      if (match) return match;
    }
    return list[attemptIdx];
  }, [txHistory, attemptIdx, currentSo?.call_id]);
  const outputs = (currentSo?.outputs ?? {}) as Record<string, unknown>;

  const memberName = formatPersonName(callRow?.memberFirstName, callRow?.memberLastName);

  const scheduledForDetail = (() => {
    const at = caseData?.outbound_scheduled_for;
    if (!at || !String(at).trim()) return undefined;
    const s = String(at).trim();
    const last = (caseData.last_vapi_call_id && String(caseData.last_vapi_call_id).trim()) || "";
    const cid = (currentSo?.call_id && String(currentSo.call_id).trim()) || "";
    if (!soHistory.length) return s;
    if (last && cid === last) return s;
    return undefined;
  })();

  return (
    <Sheet open={!!callRow} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex h-full max-h-[100dvh] flex-col overflow-hidden sm:max-w-[min(50vw,720px)]">
        <SheetHeader>
          <SheetTitle>Call Details</SheetTitle>
        </SheetHeader>

        {callRow && caseData && (
          <div className="mt-4 flex min-h-0 flex-1 flex-col space-y-5 overflow-y-auto pr-1">
            {/* Case & Call IDs */}
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Case ID</div>
              <div className="font-mono text-sm break-all">{caseData.id}</div>
              {currentSo?.call_id && (
                <>
                  <div className="text-xs text-muted-foreground mt-2">Call ID</div>
                  <div className="font-mono text-sm break-all">
                    {currentSo.call_id}
                  </div>
                </>
              )}
            </div>

            <AttemptSelector
              attempts={soHistory}
              value={attemptIdx}
              onChange={setAttemptIdx}
            />

            {/* Status Badges */}
            <div className="flex flex-wrap gap-2">
              <ResultBadge success={currentSo?.success ?? callRow.success} />
              {(outputs.pa_status as string) && (
                <EnumBadge value={`PA: ${String(outputs.pa_status)}`} />
              )}
              {(outputs.fail_reason as string) && (
                <EnumBadge
                  value={String(outputs.fail_reason)}
                  variant="destructive"
                />
              )}
            </div>

            {/* Detail text */}
            {((outputs.pa_details as string) || (outputs.fail_details as string)) && (
              <p className="text-sm">
                {(outputs.pa_details as string) || (outputs.fail_details as string)}
              </p>
            )}

            {/* Reference number */}
            {(outputs.reference_number as string) && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Reference #:</span>
                <span className="font-mono font-medium">
                  {outputs.reference_number as string}
                </span>
              </div>
            )}

            <Separator />

            <CallInfoSection
              phoneNumber={
                currentTx?.phone_number ?? currentSo?.phone_number ?? callRow.phoneNumber
              }
              duration={outputs.call_duration as number | undefined}
              timestamp={currentTx?.timestamp ?? currentSo?.timestamp}
              scheduledFor={scheduledForDetail}
            />

            <Separator />

            <PatientInfoSection
              name={memberName}
              dob={callRow.memberDob ?? "-"}
              memberId={callRow.memberId ?? "-"}
              payer={callRow.payerName ?? "-"}
              medication={callRow.productName ?? "-"}
            />

            <Separator />

            {/* Structured Output */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Structured Output</h4>
              <StructuredOutputView outputs={outputs} />
            </div>

            <Separator />

            {/* Transcript */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Transcript</h4>
              {currentTx?.transcript ? (
                <TranscriptView messages={currentTx.transcript} />
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No transcript for this attempt
                </p>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
